import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { useRealtime } from './RealtimeContext';
import { useToast } from './ToastContext';
import { useDesktopNotifications } from '../hooks/useDesktopNotifications';
import {
  deleteNotification as apiDeleteNotification,
  deleteAllReadNotifications as apiDeleteAllRead,
  getUnreadCount as apiGetUnreadCount,
  listNotifications as apiListNotifications,
  markAllNotificationsRead as apiMarkAllRead,
  markNotificationRead as apiMarkRead,
} from '../services/notifications';

export const NotificationContext = createContext(null);

const RECENT_LIMIT = 10;

/**
 * Holds the user's notification state (unread count + recent feed) and keeps it in sync
 * with the backend via REST (initial load + on-demand) and WebSocket (push + cross-tab).
 */
export function NotificationProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const { subscribe, connected } = useRealtime();
  const { addToast } = useToast();
  const { show: showDesktopNotification } = useDesktopNotifications();

  const [unreadCount, setUnreadCount] = useState(0);
  const [recent, setRecent] = useState([]);  // 10 most recent (any read state, non-deleted)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const initLoadedRef = useRef(false);

  // ─── Initial load on auth ───────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const [page, count] = await Promise.all([
        apiListNotifications({ status: 'all', page: 0, size: RECENT_LIMIT }),
        apiGetUnreadCount(),
      ]);
      setRecent(page.items);
      setUnreadCount(count);
      setError(null);
    } catch (err) {
      console.warn('[notifications] refresh failed', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      setRecent([]);
      initLoadedRef.current = false;
      return;
    }
    if (!initLoadedRef.current) {
      initLoadedRef.current = true;
      refresh();
    }
  }, [isAuthenticated, refresh]);

  // Keep a ref of the latest `recent` so the read-sync effect can read current state
  // without including `recent` in its dependency array (which would cause re-subscribe churn).
  const recentRef = useRef(recent);
  useEffect(() => { recentRef.current = recent; }, [recent]);

  // Keep a ref of addToast so the WS subscription effect doesn't re-subscribe on toast changes.
  const addToastRef = useRef(addToast);
  useEffect(() => { addToastRef.current = addToast; }, [addToast]);

  // Same ref pattern for the desktop-notification trigger — the hook returns a new
  // function reference whenever its internal opt-in state flips.
  const showDesktopRef = useRef(showDesktopNotification);
  useEffect(() => { showDesktopRef.current = showDesktopNotification; }, [showDesktopNotification]);

  // ─── WebSocket: new notifications ───────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated || !connected) return;
    const unsubscribe = subscribe('/user/queue/notifications', (n) => {
      // Prepend; trim to RECENT_LIMIT.
      setRecent((prev) => {
        if (prev.some((r) => r.id === n.id)) return prev;  // dedupe
        return [n, ...prev].slice(0, RECENT_LIMIT);
      });
      if (n.readAt == null) {
        setUnreadCount((c) => c + 1);
      }
      // Surface ACTION_REQUIRED as a toast AND, if the user opted in, a browser
      // desktop notification (the hook itself enforces permission + visibility guards).
      // WARNING gets a softer in-app toast only. INFO is intentionally silent.
      if (n.severity === 'ACTION_REQUIRED') {
        addToastRef.current(n.summary, 'error', 5000);
        showDesktopRef.current({
          title: 'Action required',
          body: n.summary,
          tag: `cts-${n.id}`,
          onClick: () => {
            if (typeof window !== 'undefined') window.focus();
          },
        });
      } else if (n.severity === 'WARNING') {
        addToastRef.current(n.summary, 'info', 4000);
      }
    });
    return unsubscribe;
  }, [isAuthenticated, connected, subscribe]);

  // ─── WebSocket: cross-tab read/delete sync ──────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated || !connected) return;
    const unsubscribe = subscribe('/user/queue/notifications.read', (evt) => {
      if (!evt || !evt.type) return;
      switch (evt.type) {
        case 'notification.read': {
          const id = evt.id;
          setRecent((prev) => prev.map((n) =>
            n.id === id && n.readAt == null ? { ...n, readAt: new Date().toISOString() } : n
          ));
          setUnreadCount((c) => {
            // Only decrement if this row was actually unread in our cache.
            const row = recentRef.current.find((r) => r.id === id);
            return row && !row.readAt ? Math.max(0, c - 1) : c;
          });
          break;
        }
        case 'notification.read.all': {
          setRecent((prev) => prev.map((n) =>
            n.readAt == null ? { ...n, readAt: new Date().toISOString() } : n
          ));
          setUnreadCount(0);
          break;
        }
        case 'notification.deleted': {
          const id = evt.id;
          setRecent((prev) => prev.filter((n) => n.id !== id));
          // unreadCount adjustment handled implicitly by row removal; refetch count for accuracy
          apiGetUnreadCount().then(setUnreadCount).catch(() => {});
          break;
        }
        case 'notification.deleted.all': {
          setRecent((prev) => prev.filter((n) => n.readAt == null));
          apiGetUnreadCount().then(setUnreadCount).catch(() => {});
          break;
        }
        default:
          break;
      }
    });
    return unsubscribe;
  }, [isAuthenticated, connected, subscribe]);

  // ─── Mutator wrappers ───────────────────────────────────────────────────────
  const markRead = useCallback(async (id) => {
    // Optimistic update; the server will also emit a notifications.read event that
    // this tab receives (idempotent thanks to the readAt check in the handler).
    setRecent((prev) => prev.map((n) =>
      n.id === id && n.readAt == null ? { ...n, readAt: new Date().toISOString() } : n
    ));
    setUnreadCount((c) => {
      const row = recentRef.current.find((r) => r.id === id);
      return row && !row.readAt ? Math.max(0, c - 1) : c;
    });
    try {
      await apiMarkRead(id);
    } catch (err) {
      console.warn('[notifications] markRead failed; refetching', err);
      await refresh();
    }
  }, [refresh]);

  const markAllRead = useCallback(async () => {
    setRecent((prev) => prev.map((n) => n.readAt == null ? { ...n, readAt: new Date().toISOString() } : n));
    setUnreadCount(0);
    try {
      await apiMarkAllRead();
    } catch (err) {
      console.warn('[notifications] markAllRead failed; refetching', err);
      await refresh();
    }
  }, [refresh]);

  const softDelete = useCallback(async (id) => {
    setRecent((prev) => prev.filter((n) => n.id !== id));
    try {
      await apiDeleteNotification(id);
    } catch (err) {
      console.warn('[notifications] softDelete failed; refetching', err);
      await refresh();
    }
  }, [refresh]);

  const deleteAllRead = useCallback(async () => {
    setRecent((prev) => prev.filter((n) => n.readAt == null));
    try {
      await apiDeleteAllRead();
    } catch (err) {
      console.warn('[notifications] deleteAllRead failed; refetching', err);
      await refresh();
    }
  }, [refresh]);

  const value = {
    unreadCount,
    recent,
    loading,
    error,
    refresh,
    markRead,
    markAllRead,
    softDelete,
    deleteAllRead,
  };

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
}
